import { ref, onMounted } from 'vue'
import api from '@/store/api'

const PUSH_PROMPTED_STORAGE_PREFIX = 'bottle:push-prompted:v2:'

// We only ever prompt for push permission on mobile devices (phones + tablets).
// On desktop the toggle/auto-prompt should be a no-op for permission, even though
// the underlying notification setting still saves.
export const isMobileDevice = (() => {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) return true
  // iPadOS Safari reports as "Macintosh"; differentiate via multi-touch.
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return true
  return false
})()

export function markPushPrompted(userId) {
  if (!userId) return
  localStorage.setItem(`${PUSH_PROMPTED_STORAGE_PREFIX}${userId}`, '1')
}

export function clearPushPrompted(userId) {
  if (!userId) return
  localStorage.removeItem(`${PUSH_PROMPTED_STORAGE_PREFIX}${userId}`)
}

export function hasBeenPushPrompted(userId) {
  if (!userId) return false
  return localStorage.getItem(`${PUSH_PROMPTED_STORAGE_PREFIX}${userId}`) !== null
}

const permissionState = ref(typeof Notification !== 'undefined' ? Notification.permission : 'default')
const isSubscribed = ref(false)
const isLoading = ref(false)
const isSupported = ref(false)

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

// Persist a browser PushSubscription on the server. Idempotent: the backend
// upserts by endpoint, so calling this for an already-registered subscription
// is a no-op row-wise.
async function persistSubscription(subscription) {
  const keys = subscription.toJSON()
  await api.call(
    'POST',
    'web_push_subscriptions',
    {
      web_push_subscription: {
        endpoint: keys.endpoint,
        p256dh: keys.keys.p256dh,
        auth: keys.keys.auth
      }
    },
    { receive: false }
  )
}

// Create a fresh browser PushSubscription using the server's VAPID key.
async function createBrowserSubscription() {
  const response = await api.call('GET', 'vapid_public_key', {}, { receive: false })
  const vapidPublicKey = response.data.vapid_public_key
  if (!vapidPublicKey) throw new Error('No VAPID key configured')

  const registration = await navigator.serviceWorker.ready
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
  })
}

// Runs on every app load (AppSidebar mounts the composable). Keeps the server's
// record of this device's push subscription in sync with the browser, healing
// merchants who silently lost it:
//   - browser still has a subscription the server pruned  -> re-register it
//   - the push service rotated/expired the subscription   -> getSubscription()
//     returns null; if the user previously granted permission, mint a fresh one
//     and register it, rather than leaving them permanently unsubscribed.
export async function checkExistingSubscription() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return
  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()

    if (!subscription) {
      const granted = typeof Notification !== 'undefined' && Notification.permission === 'granted'
      if (!granted) {
        isSubscribed.value = false
        return
      }
      const fresh = await createBrowserSubscription()
      await persistSubscription(fresh)
      isSubscribed.value = true
      return
    }

    // Verify the subscription still exists on the server
    const response = await api.call(
      'POST',
      'web_push_subscriptions/exists',
      {
        endpoint: subscription.endpoint
      },
      { receive: false }
    )
    if (response.data?.exists) {
      isSubscribed.value = true
    } else {
      // Server doesn't know about this subscription — re-register it
      await persistSubscription(subscription)
      isSubscribed.value = true
    }
  } catch (e) {
    // ignore
  }
}

export function usePushNotifications() {
  onMounted(() => {
    isSupported.value = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
    permissionState.value = typeof Notification !== 'undefined' ? Notification.permission : 'default'
    checkExistingSubscription()
  })

  async function subscribe() {
    if (isLoading.value) return
    isLoading.value = true

    try {
      const permission = await Notification.requestPermission()
      permissionState.value = permission
      if (permission !== 'granted') return

      const subscription = await createBrowserSubscription()
      await persistSubscription(subscription)

      isSubscribed.value = true
    } catch (e) {
      console.error('Push subscription failed:', e)
    } finally {
      isLoading.value = false
    }
  }

  async function unsubscribe() {
    if (isLoading.value) return
    isLoading.value = true

    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      if (subscription) {
        await subscription.unsubscribe()
      }
      isSubscribed.value = false
    } catch (e) {
      console.error('Push unsubscribe failed:', e)
    } finally {
      isLoading.value = false
    }
  }

  return {
    permissionState,
    isSubscribed,
    isLoading,
    isSupported,
    subscribe,
    unsubscribe
  }
}
